// src/features/Analytics/mlAnomalyDetector.js
import { MLDataPreprocessor } from '../../utils/mlDataPreprocessor';

export class MLAnomalyDetector {
  constructor() {
    this.trainingData = [];
    this.thresholds = {};
    this.anomalies = [];
  }

  // Завантаження та аналіз даних
  analyzeData(records, t) {
    const processed = records
      .map(record => MLDataPreprocessor.normalizeRecord(record))
      .filter(record => record !== null && record.dataQuality.isComplete);
    
    this.trainingData = processed;
    this.calculateThresholds();
    this.detectAnomalies(t);
    
    return this.getAnomalyReport(t);
  }

  // Розрахунок нормальних діапазонів
  calculateThresholds() {
    if (this.trainingData.length === 0) return;

    const metrics = {
      production: this.trainingData.map(d => d.productionFeatures.totalProduction),
      water: this.trainingData.map(d => d.resourceFeatures.waterConsumption).filter(v => v > 0),
      propane: this.trainingData.map(d => d.resourceFeatures.propaneConsumption).filter(v => v > 0),
      efficiency: this.trainingData.map(d => d.productionFeatures.efficiency).filter(v => v > 0)
    };

    this.thresholds = {};

    Object.entries(metrics).forEach(([metric, values]) => {
      if (values.length < 3) return;

      const mean = values.reduce((a, b) => a + b) / values.length;
      const std = Math.sqrt(values.reduce((sq, n) => sq + Math.pow(n - mean, 2), 0) / values.length);
      
      this.thresholds[metric] = {
        mean,
        std,
        upper: mean + (2 * std), // 95% довіри
        lower: Math.max(0, mean - (2 * std)),
        normalRange: [mean - std, mean + std]
      };
    });
  }

  // Виявлення аномалій
  detectAnomalies(t) {
    this.anomalies = [];

    this.trainingData.forEach(record => {
      const recordAnomalies = [];
      
      // Перевірка виробництва
      if (this.thresholds.production) {
        const production = record.productionFeatures.totalProduction;
        if (production > this.thresholds.production.upper || 
            production < this.thresholds.production.lower) {
          recordAnomalies.push({
            type: 'production',
            value: production,
            expected: this.thresholds.production.mean,
            severity: production === 0 ? 'high' : 'medium',
            message: this.getAnomalyMessage('production', production, t)
          });
        }
      }

      // Перевірка споживання води
      if (this.thresholds.water && record.resourceFeatures.waterConsumption > 0) {
        const water = record.resourceFeatures.waterConsumption;
        if (water > this.thresholds.water.upper) {
          recordAnomalies.push({
            type: 'water_consumption',
            value: water,
            expected: this.thresholds.water.mean,
            severity: 'high',
            message: this.getAnomalyMessage('water_high', water, t)
          });
        }
      }

      // Перевірка споживання пропану
      if (this.thresholds.propane && record.resourceFeatures.propaneConsumption > 0) {
        const propane = record.resourceFeatures.propaneConsumption;
        if (propane > this.thresholds.propane.upper) {
          recordAnomalies.push({
            type: 'propane_consumption', 
            value: propane,
            expected: this.thresholds.propane.mean,
            severity: 'medium',
            message: this.getAnomalyMessage('propane_high', propane, t)
          });
        }
      }

      // Перевірка ефективності
      if (this.thresholds.efficiency && record.productionFeatures.efficiency > 0) {
        const efficiency = record.productionFeatures.efficiency;
        if (efficiency < this.thresholds.efficiency.lower) {
          recordAnomalies.push({
            type: 'low_efficiency',
            value: efficiency,
            expected: this.thresholds.efficiency.mean,
            severity: 'high',
            message: this.getAnomalyMessage('efficiency_low', efficiency, t)
          });
        }
      }

      // Перевірка співвідношення ресурсів
      if (record.productionFeatures.totalProduction > 0 && 
          record.resourceFeatures.waterConsumption > 0) {
        const waterRatio = record.productionFeatures.totalProduction / record.resourceFeatures.waterConsumption;
        if (waterRatio < 0.1) { // Менше 100г виробництва на літр води
          recordAnomalies.push({
            type: 'water_inefficiency',
            value: waterRatio,
            expected: 0.2, // Нормальне співвідношення
            severity: 'medium',
            message: this.getAnomalyMessage('water_inefficient', waterRatio, t)
          });
        }
      }

      if (recordAnomalies.length > 0) {
        this.anomalies.push({
          date: record.date,
          record,
          anomalies: recordAnomalies,
          totalSeverity: this.calculateOverallSeverity(recordAnomalies)
        });
      }
    });
  }

  // Повідомлення про аномалії з підтримкою перекладів
  getAnomalyMessage(type, value, t) {
    const messages = {
      production: t('anomaly_message_production', { value }),
      water_high: t('anomaly_message_water_high', { value }),
      propane_high: t('anomaly_message_propane_high', { value }), 
      efficiency_low: t('anomaly_message_efficiency_low', { value: value.toFixed(1) }),
      water_inefficient: t('anomaly_message_water_inefficient', { value: value.toFixed(3) })
    };

    return messages[type] || `${t('anomaly_detected')}: ${value}`;
  }

  // Розрахунок загальної серйозності
  calculateOverallSeverity(anomalies) {
    const severityWeights = { high: 3, medium: 2, low: 1 };
    const totalScore = anomalies.reduce((score, anomaly) => 
      score + (severityWeights[anomaly.severity] || 1), 0
    );
    
    if (totalScore >= 6) return 'critical';
    if (totalScore >= 4) return 'high';
    if (totalScore >= 2) return 'medium';
    return 'low';
  }

  // Звіт про аномалії
  getAnomalyReport(t) {
    const criticalAnomalies = this.anomalies.filter(a => a.totalSeverity === 'critical');
    const highAnomalies = this.anomalies.filter(a => a.totalSeverity === 'high');
    
    return {
      totalAnomalies: this.anomalies.length,
      criticalCount: criticalAnomalies.length,
      highCount: highAnomalies.length,
      anomalies: this.anomalies,
      thresholds: this.thresholds,
      summary: this.getSummary(t),
      recommendations: this.generateRecommendations(t)
    };
  }

  // Загальний звіт з підтримкою перекладів
  getSummary(t) {
    if (this.anomalies.length === 0) {
      return t('anomaly_no_issues');
    }

    const anomalyTypes = {};
    this.anomalies.forEach(anomaly => {
      anomaly.anomalies.forEach(a => {
        anomalyTypes[a.type] = (anomalyTypes[a.type] || 0) + 1;
      });
    });

    const topIssues = Object.entries(anomalyTypes)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 3)
      .map(([type, count]) => `${this.getAnomalyTypeName(type, t)}: ${count}`)
      .join(', ');

    return t('anomaly_total_found', { count: this.anomalies.length }) + `. Основні проблеми: ${topIssues}`;
  }

  // Назви типів аномалій з підтримкою перекладів
  getAnomalyTypeName(type, t) {
    const names = {
      production: t('anomaly_type_production'),
      water_consumption: t('anomaly_type_water'),
      propane_consumption: t('anomaly_type_propane'),
      low_efficiency: t('anomaly_type_efficiency'),
      water_inefficiency: t('anomaly_type_water')
    };
    
    return names[type] || type;
  }

  // Генерація рекомендацій з підтримкою перекладів
  generateRecommendations(t) {
    const recommendations = [];
    
    // Аналізуємо аномалії для конкретних рекомендацій
    const waterAnomalies = this.anomalies.filter(a => 
      a.anomalies.some(an => an.type.includes('water'))
    );
    
    const efficiencyAnomalies = this.anomalies.filter(a =>
      a.anomalies.some(an => an.type.includes('efficiency'))
    );

    if (waterAnomalies.length > 2) {
      recommendations.push({
        type: 'water_optimization',
        priority: 'high',
        title: t('resource_optimization'),
        description: t('consumption_anomaly_high') + ' ' + t('resource_water'),
        actions: [
          t('check_equipment') + ' ' + t('resource_water'),
          t('optimize_consumption') + ' ' + t('resource_water'),
          t('anomaly_action_review') + ' ' + t('resource_water')
        ]
      });
    }

    if (efficiencyAnomalies.length > 0) {
      recommendations.push({
        type: 'efficiency_improvement', 
        priority: 'medium',
        title: t('resource_efficiency') + ' ' + t('recommendation_medium_priority'),
        description: t('efficiency_anomaly_low'),
        actions: [
          t('anomaly_action_optimize'),
          t('check_equipment'),
          t('anomaly_action_review')
        ]
      });
    }

    // Загальні рекомендації
    if (this.anomalies.length > 5) {
      recommendations.push({
        type: 'general_maintenance',
        priority: 'medium',
        title: t('recommendation_medium_priority') + ' ' + t('anomaly_action_review'),
        description: t('anomaly_total_found', { count: this.anomalies.length }) + '. ' + t('check_equipment'),
        actions: [
          t('check_equipment'),
          t('anomaly_action_review'),
          t('anomaly_action_optimize')
        ]
      });
    }

    // Додаємо переклади для пріоритетів
    recommendations.forEach(rec => {
      rec.priorityText = this.getPriorityText(rec.priority, t);
    });

    return recommendations;
  }

  // Текст пріоритету з перекладами
  getPriorityText(priority, t) {
    const priorities = {
      high: t('recommendation_high_priority'),
      medium: t('recommendation_medium_priority'),
      low: t('recommendation_low_priority')
    };
    return priorities[priority] || priority;
  }

  // Перевірка нового запису в реальному часі
  checkNewRecord(record, t) {
    const processed = MLDataPreprocessor.normalizeRecord(record);
    if (!processed) return null;

    const anomalies = [];
    
    // Перевірка проти встановлених порогів
    Object.entries(this.thresholds).forEach(([metric, threshold]) => {
      const value = this.getValueFromRecord(processed, metric);
      if (value !== null && (value > threshold.upper || value < threshold.lower)) {
        anomalies.push({
          type: metric,
          value,
          expected: threshold.mean,
          severity: value === 0 ? 'high' : 'medium',
          message: t('anomaly_alert_new') + ': ' + this.getAnomalyMessage(metric, value, t)
        });
      }
    });

    return anomalies.length > 0 ? {
      record: processed,
      anomalies,
      timestamp: new Date()
    } : null;
  }

  // Отримання значення з запису
  getValueFromRecord(record, metric) {
    const valueMap = {
      production: record.productionFeatures.totalProduction,
      water: record.resourceFeatures.waterConsumption,
      propane: record.resourceFeatures.propaneConsumption,
      efficiency: record.productionFeatures.efficiency
    };
    
    return valueMap[metric] || null;
  }

  // Отримання статистики моделі
  getModelStats() {
    return {
      trainingDataSize: this.trainingData.length,
      thresholdsCalculated: Object.keys(this.thresholds).length,
      anomaliesDetected: this.anomalies.length,
      metrics: Object.keys(this.thresholds)
    };
  }

  // Скидання моделі
  reset() {
    this.trainingData = [];
    this.thresholds = {};
    this.anomalies = [];
  }
}

export default MLAnomalyDetector;


